// capture_recovery_dump — server-side inbox for device-held orphaned counts.
//
// PHASE 1 (RESCUE) of the offline-capture-loss recovery. When an offline
// capture can never sync — its parent capture_session / building / space was
// removed or re-seeded server-side (a re-seed or data clear changed the ids the
// device had cached) — the PowerSync connector preserves the count on-device
// in the local-only `orphaned_capture_items` table (connector-quarantine.ts)
// rather than dropping it. There is no in-app re-inject path, and field
// devices we cannot physically access hold the ONLY copy. This table is the
// destination of a one-shot client "rescue" (POST /capture/recovery-dump): the
// device reads its held counts and ships them here, lifting the contractual
// baseline off the device into durable server storage.
//
// This is RESCUE only — a faithful, append-only inbox. It is NOT live capture
// data: nothing reads it back into capture_items, the dashboard, the CSV
// export, the review queue, breakdowns, or sync. PHASE 2 (PLACEMENT) maps these
// rescued rows onto current building+rooms under review + sign-off, separately.
//
// Design choices for a last-resort recovery inbox:
//   - NO foreign keys to the source graph (capture_sessions / sites /
//     buildings / spaces / asset_types). The whole point is that those rows
//     are GONE or changed — an inbox row must never be blocked or cascaded by
//     the live graph. (Same stance as deleted_capture_items.) The only FK is
//     `user_id -> users.id` (restrict): the dumping user is a live, authed
//     user we preserve for the audit trail.
//   - Every device-sourced reference / value column is `text` (or nullable
//     integer), NOT `uuid` / `numeric` / `timestamptz`. An insert must NEVER
//     reject a held count on a type/parse mismatch — rejecting is losing the
//     very data we exist to capture. Phase 2 casts/validates when it places.
//   - `raw` jsonb keeps the verbatim client row as a forensic backstop, so a
//     column we mis-mapped is still fully recoverable. (This table is NOT
//     synced, so the "no nested JSON for synced data" rule does not apply.)
//   - Idempotent: unique (user_id, original_item_id) so a device re-running
//     the rescue upserts instead of duplicating.
//
// PowerSync: deliberately NOT added to the client mirror or sync-config — it is
// a server-only inbox; syncing it would push recovered snapshots back to
// devices. (Same as deleted_capture_items; the schema-drift gate only inspects
// client-mirrored tables, so an unsynced server table is correctly ignored.)

import {
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core'

import { users } from './schema'

export const captureRecoveryDump = pgTable(
  'capture_recovery_dump',
  {
    // Inbox-row identity — independent of the held item's id, so the archive
    // id is never confused with a live/local capture_items id.
    id: uuid('id').primaryKey().defaultRandom(),

    // Who dumped it (the authenticated caller) — the one live FK. Restrict so
    // the acting user cannot be removed while they still own recovery rows.
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    userDisplayName: text('user_display_name').notNull(),
    // capture_sessions.source_device_id of the held row's session, if known.
    sourceDeviceId: text('source_device_id'),

    // --- held count, verbatim from the device (text-only, never rejects) ---
    // original_item_id is the local capture_items id (orphaned_capture_items.id)
    // and the idempotency key — the one field the client must always supply.
    originalItemId: text('original_item_id').notNull(),
    captureSessionId: text('capture_session_id'),
    siteId: text('site_id'),
    buildingId: text('building_id'),
    spaceId: text('space_id'),
    assetTypeId: text('asset_type_id'),
    quantity: text('quantity'),
    isZeroConfirmed: integer('is_zero_confirmed'),
    notes: text('notes'),
    capturedAt: text('captured_at'),
    orphanedAt: text('orphaned_at'),
    reason: text('reason'),

    // --- room/session context for Phase 2 placement, read from the device's
    // still-local spaces / capture_sessions (null when already gone locally) ---
    spaceDisplayName: text('space_display_name'),
    roomTypeId: text('room_type_id'),
    floorId: text('floor_id'),
    serialNumber: integer('serial_number'),
    sessionBuildingId: text('session_building_id'),
    sessionSiteId: text('session_site_id'),
    sessionStartedAt: text('session_started_at'),

    // Verbatim client row — forensic backstop against column mis-mapping.
    raw: jsonb('raw'),

    // This inbox row's own server write time (distinct from the held row's
    // captured_at / orphaned_at, which are device timestamps above).
    recoveredAt: timestamp('recovered_at', { withTimezone: true })
      .notNull()
      .defaultNow(),

    // --- Phase 2 (PLACEMENT) bookkeeping (place-recovered-counts.ts) ---
    // Set when this rescued row has been placed back into capture_items. The
    // resulting capture_items.id reuses originalItemId, so placement is
    // naturally idempotent on the capture_items PK; `placedAt` additionally
    // marks the inbox row done so a placement re-run SKIPS it (never double-
    // counts the contractual baseline). `placedByUserId` is the admin actor
    // who placed it (audit). All three are NULL for an un-placed inbox row
    // (the Phase-1-only state) — additive, so existing rescued rows are
    // untouched. Nullable + no NOT NULL backfill: this is a forward-only
    // lifecycle flag, never a data-loss vector.
    placedAt: timestamp('placed_at', { withTimezone: true }),
    placedByUserId: uuid('placed_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    placedCaptureItemId: uuid('placed_capture_item_id'),
  },
  (t) => ({
    // Idempotency: one inbox row per (user, held item). A device re-running
    // the rescue upserts on this key instead of duplicating the count.
    userItemUnique: uniqueIndex('capture_recovery_dump_user_item_unique').on(
      t.userId,
      t.originalItemId,
    ),
    userIdx: index('capture_recovery_dump_user_id_idx').on(t.userId),
    buildingIdx: index('capture_recovery_dump_building_id_idx').on(
      t.buildingId,
    ),
  }),
)

// Plain insert-row type derived from the table (ADR-007) so the frontend rescue
// shares one source of truth instead of a hand-maintained interface that could
// silently drift from the schema. (audit-1 #10)
export type CaptureRecoveryDumpInsert = typeof captureRecoveryDump.$inferInsert
